'use client';

import React from 'react';
import { CheckIcon } from '@milanote/roma-icons';
import { cx } from '../../../utils/cx';
import { KeyboardShortcut } from '../../base/keyboardShortcut';
import type { KeyboardShortcutPlatformVariant } from '../../base/keyboardShortcut';
import { Menu } from '../../base/menu';
import type { MenuSize } from '../../base/menu';
import { Popover } from '../../base/popover';
import type { ActionMenuItemNode, MenuNode } from './actionMenuTypes';

const styles = {
    width: 'w-fit min-w-60 max-w-74',
} as const;

const bodyStylesBySize: Record<MenuSize, string> = {
    xs: '',
    sm: '',
    md: '',
    lg: 'py-lg [--roma-popover-body-padding-x:var(--spacing-lg)]',
};

const getTrailing = (
    node: ActionMenuItemNode,
    showShortcuts: boolean,
    platformVariant: KeyboardShortcutPlatformVariant,
): React.ReactNode => {
    if (node.isSelected) return <CheckIcon aria-hidden="true" className="size-4" />;
    if (!showShortcuts || !node.shortcut) return undefined;
    return <KeyboardShortcut.Group parts={node.shortcut} preferSymbol platformVariant={platformVariant} />;
};

/** Renders one resolved menu node, recursively composing submenus when needed. */
const ResolvedActionMenuNode: React.FC<{
    node: MenuNode;
    size: MenuSize;
    showIcons: boolean;
    showShortcuts: boolean;
    platformVariant: KeyboardShortcutPlatformVariant;
}> = ({ node, size, showIcons, showShortcuts, platformVariant }) => {
    if (node.type === 'divider') return <Menu.Separator />;

    if (node.customContent) {
        return (
            <Menu.CustomItem
                id={node.id}
                textValue={node.label}
                onAction={node.onAction}
                keepOpenOnSelect={node.keepOpenOnSelect}
                isDisabled={node.isDisabled}
            >
                {node.customContent}
            </Menu.CustomItem>
        );
    }

    const item = (
        <Menu.Item
            id={node.id}
            shellClassName="gap-xl"
            label={node.label}
            leading={showIcons ? node.leading : undefined}
            trailing={getTrailing(node, showShortcuts, platformVariant)}
            onAction={node.onAction}
            keepOpenOnSelect={node.keepOpenOnSelect}
            isDisabled={node.isDisabled}
        />
    );

    if (!node.children) return item;

    return (
        <Menu.Submenu>
            {item}
            <Menu.SubmenuPopover className={styles.width} data-cursor-anchored-menu-popover>
                <Popover.Body className={bodyStylesBySize[size]}>
                    <Menu aria-label={node.label} size={size}>
                        {node.children.map((child) => (
                            <ResolvedActionMenuNode
                                key={`${child.type}:${child.id}`}
                                node={child}
                                size={size}
                                showIcons={showIcons}
                                showShortcuts={showShortcuts}
                                platformVariant={platformVariant}
                            />
                        ))}
                    </Menu>
                </Popover.Body>
            </Menu.SubmenuPopover>
        </Menu.Submenu>
    );
};

export type ActionMenuContentProps = {
    'aria-label': string;
    nodes: MenuNode[];
    autoFocus?: boolean;
    size?: MenuSize;
    className?: string;
    showIcons?: boolean;
    showShortcuts?: boolean;
    platformVariant?: KeyboardShortcutPlatformVariant;
};

export type ActionMenuProps = ActionMenuContentProps;

/** Renders resolved action-menu nodes inside an owning popover surface. */
const ActionMenuContent: React.FC<ActionMenuContentProps> = ({
    nodes,
    autoFocus,
    size = 'xs',
    className,
    showIcons = false,
    showShortcuts = true,
    platformVariant = 'apple',
    ...props
}) => (
    <Popover.Body className={cx(bodyStylesBySize[size], className)}>
        <Menu aria-label={props['aria-label']} autoFocus={autoFocus} size={size}>
            {nodes.map((node) => (
                <ResolvedActionMenuNode
                    key={`${node.type}:${node.id}`}
                    node={node}
                    size={size}
                    showIcons={showIcons}
                    showShortcuts={showShortcuts}
                    platformVariant={platformVariant}
                />
            ))}
        </Menu>
    </Popover.Body>
);

/** Renders resolved application action-menu nodes on a static Roma popover surface. */
const ActionMenuShell: React.FC<ActionMenuProps> = ({ className = styles.width, size = 'xs', ...props }) => (
    <Popover.Base className={className}>
        <ActionMenuContent {...props} size={size} />
    </Popover.Base>
);

export const ActionMenu = Object.assign(ActionMenuShell, { Content: ActionMenuContent, styles });
